import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useLang } from "@/lib/useLang";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const pickLang = (poll, key, lang) => {
  if (lang === "ml") {
    const ml = poll?.[`${key}_ml`];
    if (ml && String(ml).trim()) return ml;
  }
  return poll?.[key] || "";
};

/**
 * Auto-rotating hero slider of all active polls.
 * Closed/expired polls are filtered out server-side.
 * If only 1 poll, renders as a single static slide (no auto-rotate).
 */
export const PollHeroSlider = () => {
  const { lang, t } = useLang();
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(0);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/polls`)
      .then((r) => {
        if (!cancelled) setPolls(r.data?.polls || []);
      })
      .catch(() => { /* slider just hides */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Auto-rotate every 6s if 2+ polls
  useEffect(() => {
    if (polls.length < 2) return undefined;
    const id = setInterval(() => setActive((i) => (i + 1) % polls.length), 6000);
    return () => clearInterval(id);
  }, [polls.length]);

  if (loading || polls.length === 0) return null;

  const current = polls[active];
  const isMC = current.poll_type === "multiple_choice";
  const headline = pickLang(current, "headline", lang) || pickLang(current, "title", lang);
  const subtext = pickLang(current, "subtext", lang);
  const category = pickLang(current, "category", lang);
  const subShown = subtext?.length > 200 ? `${subtext.slice(0, 200)}…` : subtext;

  return (
    <section
      className="brutal-card p-6 md:p-8 relative overflow-hidden"
      style={{ background: "#0A0A0A", color: "#FDFBF7" }}
      data-testid="hero-slider"
    >
      <div className="absolute top-0 left-0 right-0 h-1 flex">
        <div style={{ background: "#FF9933", width: "33.33%" }} />
        <div style={{ background: "#FFFFFF", width: "33.33%" }} />
        <div style={{ background: "#138808", width: "33.33%" }} />
      </div>

      <div className="flex items-center justify-between mb-3">
        <p className="text-[10px] md:text-xs uppercase font-extrabold tracking-[0.4em]" style={{ color: "#FF9933" }}>
          ★ {isMC ? t("hero_live_poll") : t("hero_open_conv")} ★ {category}
        </p>
        {polls.length > 1 && (
          <div className="flex gap-1" data-testid="hero-slider-dots">
            {polls.map((_, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                data-testid={`hero-slider-dot-${i}`}
                aria-label={`Slide ${i + 1}`}
                className="w-2 h-2 rounded-full transition-opacity"
                style={{
                  background: i === active ? "#FF9933" : "#3a3a3a",
                  opacity: i === active ? 1 : 0.7,
                }}
              />
            ))}
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-[1fr_auto] gap-6 items-center" data-testid={`hero-slide-${current.slug}`}>
        <div>
          <h2
            className="font-display uppercase leading-[0.9] tracking-tighter"
            style={{ fontSize: "clamp(1.5rem, 4.5vw, 3rem)", color: "#FDFBF7" }}
            data-testid="hero-slider-headline"
          >
            {headline}
          </h2>
          <p className="mt-3 text-sm md:text-base font-medium opacity-90 max-w-2xl" data-testid="hero-slider-subtext">
            {subShown}
          </p>
        </div>
        <Link
          to={isMC ? `/p/${current.slug}` : "/"}
          className="brutal-btn shrink-0 self-start md:self-center"
          style={{ background: "#FF9933", color: "#0A0A0A", borderColor: "#FF9933" }}
          data-testid="hero-slider-cta"
        >
          {isMC ? t("hero_cta_mc") : t("hero_cta_open")}
        </Link>
      </div>
    </section>
  );
};

export default PollHeroSlider;
